import { collection, query, where, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { User } from 'firebase/auth';
import { db } from '../firebase';
import { auth } from '../firebase';

export interface OnlineStatusUser {
  uid: string;
  displayName: string;
  email: string;
  photoURL?: string;
  isOnline: boolean;
  lastSeen: Date;
}

export interface ConnectionRequest {
  id: string;
  fromUserId: string;
  toUserId: string;
  status: 'pending' | 'accepted' | 'declined' | 'withdrawn';
  createdAt: Date;
  updatedAt: Date;
}

export interface OnlineStatusCallback {
  onUserOnline: (user: OnlineStatusUser) => void;
  onUserOffline: (user: OnlineStatusUser) => void;
  onMissedConnectionRequest: (user: OnlineStatusUser) => void;
}

export interface MissedConnectionRequest {
  userId: string;
  userName: string;
  userPhoto?: string;
  timestamp: Date;
  retryCount: number;
}

class OnlineStatusService {
  private listeners: Map<string, () => void> = new Map();
  private callbacks: OnlineStatusCallback[] = [];
  private currentUserId: string | null = null;
  private sentConnectionRequests: ConnectionRequest[] = [];
  private isInActiveChatFlow = false;
  private missedConnectionRequests: MissedConnectionRequest[] = [];
  private missedRequestCheckInterval: NodeJS.Timeout | null = null;

  constructor() {
    // Listen to auth state changes
    auth.onAuthStateChanged((user: User | null) => {
      this.currentUserId = user?.uid || null;
      if (user) {
        this.setUserOnline();
        this.loadSentConnectionRequests();
      } else {
        this.cleanup();
      }
    });

    // Set user online status when page becomes visible
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.currentUserId) {
        this.setUserOnline();
      }
    });

    // Set user online status when window gains focus
    window.addEventListener('focus', () => {
      if (this.currentUserId) {
        this.setUserOnline();
      }
    });

    // Set user offline when page is about to unload
    window.addEventListener('beforeunload', () => {
      if (this.currentUserId) {
        this.setUserOffline();
      }
    });
  }

  /**
   * Add callback for online status changes
   */
  addCallback(callback: OnlineStatusCallback): void {
    this.callbacks.push(callback);
  }

  /**
   * Remove callback for online status changes
   */
  removeCallback(callback: OnlineStatusCallback): void {
    const index = this.callbacks.indexOf(callback);
    if (index > -1) {
      this.callbacks.splice(index, 1);
    }
  }

  /**
   * Set user as online
   *
   * 🔑 UITBREIDING: bij Firestore QUIC / 'unavailable' fouten
   * proberen we het na korte tijd opnieuw, zodat tijdelijke
   * netwerkproblemen de online status niet breken.
   */
  private async setUserOnline(): Promise<void> {
    if (!this.currentUserId) return;

    try {
      const userRef = doc(db, 'userProfiles', this.currentUserId);
      await updateDoc(userRef, {
        isOnline: true,
        lastSeen: serverTimestamp(),
      });
      console.log('✅ User set as online');
    } catch (error: any) {
      console.error('❌ Error setting user online:', error);

      // 🔑 Graceful retry bij QUIC / tijdelijke Firestore fouten
      if (
        error?.message?.includes('QUIC_PROTOCOL_ERROR') ||
        error?.message?.includes('ERR_QUIC_PROTOCOL_ERROR') ||
        error?.code === 'unavailable'
      ) {
        console.log('⚠️ QUIC/unavailable error - retrying setUserOnline in 2s...');
        setTimeout(() => this.setUserOnline(), 2000);
      }
    }
  }

  /**
   * Set user as offline
   */
  private async setUserOffline(): Promise<void> {
    if (!this.currentUserId) return;

    try {
      const userRef = doc(db, 'userProfiles', this.currentUserId);
      await updateDoc(userRef, {
        isOnline: false,
        lastSeen: serverTimestamp(),
      });
      console.log('✅ User set as offline');
    } catch (error) {
      console.error('❌ Error setting user offline:', error);
    }
  }

  /**
   * Load sent connection requests for current user
   *
   * 🔑 UITBREIDING: QUIC / 'unavailable' fouten geven een
   * rustige retry in plaats van de hele flow stuk te maken.
   */
  private async loadSentConnectionRequests(): Promise<void> {
    if (!this.currentUserId) return;

    try {
      const requestsQuery = query(
        collection(db, 'connectionRequests'),
        where('fromUserId', '==', this.currentUserId),
        where('status', '==', 'pending'),
      );

      const unsubscribe = onSnapshot(
        requestsQuery,
        (snapshot) => {
          this.sentConnectionRequests = snapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
            createdAt: docSnap.data().createdAt?.toDate() || new Date(),
            updatedAt: docSnap.data().updatedAt?.toDate() || new Date(),
          })) as ConnectionRequest[];

          console.log(
            '📋 Loaded sent connection requests:',
            this.sentConnectionRequests.length,
          );

          // Wanneer de lijst verandert, refresh listeners op user-profielen
          this.refreshConnectionRequestUserListeners();
        },
        (error: any) => {
          console.error('❌ Error loading sent connection requests (listener):', error);

          if (
            error?.message?.includes('QUIC_PROTOCOL_ERROR') ||
            error?.message?.includes('ERR_QUIC_PROTOCOL_ERROR') ||
            error?.code === 'unavailable'
          ) {
            console.log(
              '⚠️ QUIC/unavailable error in requests listener - retrying loadSentConnectionRequests in 3s...',
            );
            setTimeout(() => this.loadSentConnectionRequests(), 3000);
          }
        },
      );

      this.listeners.set('sentRequests', unsubscribe);
    } catch (error: any) {
      console.error('❌ Error setting up sent connection requests listener:', error);

      if (
        error?.message?.includes('QUIC_PROTOCOL_ERROR') ||
        error?.message?.includes('ERR_QUIC_PROTOCOL_ERROR') ||
        error?.code === 'unavailable'
      ) {
        console.log(
          '⚠️ QUIC/unavailable error during listener setup - retrying loadSentConnectionRequests in 3s...',
        );
        setTimeout(() => this.loadSentConnectionRequests(), 3000);
      }
    }
  }

  /**
   * Start listening for online status of users who have pending connection requests
   */
  startListeningForConnectionRequestUsers(): void {
    this.refreshConnectionRequestUserListeners();
  }

  /**
   * Refresh listeners for all users that have a pending connection request
   * from the current user. This is called both on initial load and whenever
   * the sent requests snapshot changes.
   */
  private refreshConnectionRequestUserListeners(): void {
    if (!this.currentUserId) return;

    // First clean up any existing per-user listeners so we don't leak them
    const userListenerKeys = Array.from(this.listeners.keys()).filter((key) =>
      key.startsWith('user_'),
    );
    userListenerKeys.forEach((key) => {
      const unsubscribe = this.listeners.get(key);
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (error) {
          console.error('❌ Error unsubscribing listener for', key, error);
        }
      }
      this.listeners.delete(key);
    });

    if (this.sentConnectionRequests.length === 0) {
      console.log('ℹ️ No pending connection requests – no user listeners needed');
      return;
    }

    // Get unique user IDs from sent requests
    const userIds = Array.from(
      new Set(this.sentConnectionRequests.map((req) => req.toUserId)),
    );

    console.log(
      '🎧 Setting up online status listeners for users with pending requests:',
      userIds,
    );
    userIds.forEach((userId) => {
      if (userId) {
        this.listenToUserOnlineStatus(userId);
      }
    });
  }

  /**
   * Check if connection is already accepted between current user and target user
   */
  private async checkIfConnectionAccepted(targetUserId: string): Promise<boolean> {
    if (!this.currentUserId) return false;

    try {
      const { getDocs, query, collection, where } = await import('firebase/firestore');

      // Check if there's an accepted connection in either direction
      // Query 1: current user is user1Id
      const query1 = query(
        collection(db, 'connections'),
        where('user1Id', '==', this.currentUserId),
        where('user2Id', '==', targetUserId),
      );

      // Query 2: current user is user2Id
      const query2 = query(
        collection(db, 'connections'),
        where('user1Id', '==', targetUserId),
        where('user2Id', '==', this.currentUserId),
      );

      const [snapshot1, snapshot2] = await Promise.all([
        getDocs(query1),
        getDocs(query2),
      ]);

      // If either query returns results, connection is accepted
      const hasAcceptedConnection = snapshot1.docs.length > 0 || snapshot2.docs.length > 0;

      return hasAcceptedConnection;
    } catch (error) {
      console.error('❌ Error checking if connection is accepted:', error);
      return false; // On error, assume not accepted to be safe
    }
  }

  /**
   * Check if chatroom already exists between current user and target user
   */
  private async checkIfChatroomExists(targetUserId: string): Promise<boolean> {
    if (!this.currentUserId) return false;

    try {
      const { getDocs, query, collection, where } = await import('firebase/firestore');

      // Check if there's a chatroom with both users as participants
      const chatroomsQuery = query(
        collection(db, 'chatRooms'),
        where('participants', 'array-contains', this.currentUserId),
      );

      const snapshot = await getDocs(chatroomsQuery);

      // Check if any chatroom also includes the target user
      const hasChatroom = snapshot.docs.some((docSnap) => {
        const data = docSnap.data();
        const participants = data.participants || [];
        return participants.includes(targetUserId);
      });

      return hasChatroom;
    } catch (error) {
      console.error('❌ Error checking if chatroom exists:', error);
      return false; // On error, assume no chatroom to be safe
    }
  }

  /**
   * Listen to online status of a specific user
   */
  private listenToUserOnlineStatus(userId: string): void {
    if (this.listeners.has(`user_${userId}`)) return;

    try {
      const userRef = doc(db, 'userProfiles', userId);
      const unsubscribe = onSnapshot(userRef, (docSnap) => {
        if (docSnap.exists()) {
          const userData = docSnap.data();
          const user: OnlineStatusUser = {
            uid: docSnap.id,
            displayName: userData.displayName || 'Onbekend',
            email: userData.email || '',
            photoURL: userData.photoURL,
            isOnline: userData.isOnline || false,
            lastSeen: userData.lastSeen?.toDate() || new Date(),
          };

          // Check if user just came online
          if (user.isOnline) {
            // Find the connection request for this user
            const connectionRequest = this.sentConnectionRequests.find(
              (req) => req.toUserId === userId,
            );
            if (connectionRequest) {
              // IMPORTANT: Only show video call popup if connection is ACCEPTED, not just pending
              // The popup should only appear during the video call phase of the connection flow,
              // not immediately when a connection request is sent
              // Do this asynchronously to avoid blocking the snapshot callback
              // Store 'this' reference to avoid binding issues
              const self = this;

              // Define checkIfChatroomExists inline to avoid binding issues
              const checkChatroom = async (targetUserId: string): Promise<boolean> => {
                if (!self.currentUserId) return false;
                try {
                  const { getDocs, query, collection, where } = await import(
                    'firebase/firestore'
                  );
                  const chatroomsQueryInner = query(
                    collection(db, 'chatRooms'),
                    where('participants', 'array-contains', self.currentUserId),
                  );
                  const snapshotInner = await getDocs(chatroomsQueryInner);
                  const hasChatroomInner = snapshotInner.docs.some((docInner) => {
                    const data = docInner.data();
                    const participants = data.participants || [];
                    return participants.includes(targetUserId);
                  });
                  return hasChatroomInner;
                } catch (errorInner) {
                  console.error('❌ Error checking if chatroom exists:', errorInner);
                  return false;
                }
              };

              Promise.all([
                self.checkIfConnectionAccepted(userId),
                checkChatroom(userId),
              ])
                .then(([isConnectionAccepted, chatroomExists]) => {
                  // Only show popup if connection is ACCEPTED and chatroom exists
                  // This means they're in an active connection flow, not just a pending request
                  if (!isConnectionAccepted || !chatroomExists) {
                    console.log(
                      'ℹ️ Connection not yet accepted or no chatroom with:',
                      user.displayName,
                      '- skipping video call popup (waiting for acceptance)',
                    );
                    return;
                  }

                  // Connection is accepted and chatroom exists - they're in active flow
                  // But only show popup if we're not already in active chat flow
                  if (!self.isInActiveChatFlow) {
                    // User is online, connection is accepted, chatroom exists - show popup
                    console.log(
                      '🔔 User came online with accepted connection:',
                      user.displayName,
                      '- showing video call popup',
                    );
                    self.callbacks.forEach((callback) => {
                      callback.onUserOnline(user);
                    });
                  } else {
                    // User is online but we're in active chat flow - store as missed request
                    console.log(
                      '⏰ User came online during active chat flow:',
                      user.displayName,
                    );
                    self.addMissedConnectionRequest(user);
                  }
                })
                .catch((err) => {
                  console.error('❌ Error checking connection/chatroom status:', err);
                  // On error, don't show popup - safer to not show than to show incorrectly
                  // The popup should only appear when connection is definitely accepted
                });
            }
          }
        }
      });

      this.listeners.set(`user_${userId}`, unsubscribe);
    } catch (error) {
      console.error('❌ Error listening to user online status:', error);
    }
  }

  /**
   * Set whether user is in active chat flow
   */
  setInActiveChatFlow(inActiveFlow: boolean): void {
    const wasInActiveFlow = this.isInActiveChatFlow;
    this.isInActiveChatFlow = inActiveFlow;

    // If user just finished active chat flow, check for missed requests
    if (wasInActiveFlow && !inActiveFlow) {
      this.checkMissedConnectionRequests();
    }
  }

  /**
   * Add a missed connection request
   */
  private addMissedConnectionRequest(user: OnlineStatusUser): void {
    // Check if we already have a missed request for this user
    const existingIndex = this.missedConnectionRequests.findIndex(
      (req) => req.userId === user.uid,
    );

    if (existingIndex >= 0) {
      // Update existing missed request
      this.missedConnectionRequests[existingIndex] = {
        userId: user.uid,
        userName: user.displayName,
        userPhoto: user.photoURL,
        timestamp: new Date(),
        retryCount: this.missedConnectionRequests[existingIndex].retryCount + 1,
      };
    } else {
      // Add new missed request
      this.missedConnectionRequests.push({
        userId: user.uid,
        userName: user.displayName,
        userPhoto: user.photoURL,
        timestamp: new Date(),
        retryCount: 1,
      });
    }

    console.log('📝 Added missed connection request for:', user.displayName);
  }

  /**
   * Check for missed connection requests when chat flow ends
   */
  private checkMissedConnectionRequests(): void {
    if (this.missedConnectionRequests.length === 0) return;

    console.log(
      '🔍 Checking missed connection requests:',
      this.missedConnectionRequests.length,
    );

    // Show the most recent missed request
    const mostRecentRequest = this.missedConnectionRequests.sort(
      (a, b) => b.timestamp.getTime() - a.timestamp.getTime(),
    )[0];

    if (mostRecentRequest) {
      // Convert to OnlineStatusUser format
      const user: OnlineStatusUser = {
        uid: mostRecentRequest.userId,
        displayName: mostRecentRequest.userName,
        email: '',
        photoURL: mostRecentRequest.userPhoto,
        isOnline: true,
        lastSeen: mostRecentRequest.timestamp,
      };

      // Notify callbacks about missed request
      this.callbacks.forEach((callback) => {
        callback.onMissedConnectionRequest(user);
      });

      // Remove this missed request
      this.missedConnectionRequests = this.missedConnectionRequests.filter(
        (req) => req.userId !== mostRecentRequest.userId,
      );
    }
  }

  /**
   * Get current missed connection requests
   */
  getMissedConnectionRequests(): MissedConnectionRequest[] {
    return this.missedConnectionRequests;
  }

  /**
   * Clear all missed connection requests
   */
  clearMissedConnectionRequests(): void {
    this.missedConnectionRequests = [];
    console.log('🧹 Cleared all missed connection requests');
  }

  /**
   * Get current sent connection requests
   */
  getSentConnectionRequests(): ConnectionRequest[] {
    return this.sentConnectionRequests;
  }

  /**
   * Check if user is in active chat flow
   */
  isUserInActiveChatFlow(): boolean {
    return this.isInActiveChatFlow;
  }

  /**
   * Cleanup all listeners
   */
  private cleanup(): void {
    this.listeners.forEach((unsubscribe) => {
      unsubscribe();
    });
    this.listeners.clear();
    this.sentConnectionRequests = [];
    this.missedConnectionRequests = [];
    this.isInActiveChatFlow = false;

    if (this.missedRequestCheckInterval) {
      clearInterval(this.missedRequestCheckInterval);
      this.missedRequestCheckInterval = null;
    }
  }

  /**
   * Stop listening for a specific user
   */
  stopListeningForUser(userId: string): void {
    const listener = this.listeners.get(`user_${userId}`);
    if (listener) {
      listener();
      this.listeners.delete(`user_${userId}`);
    }
  }

  /**
   * Stop all listeners
   */
  stopAllListeners(): void {
    this.cleanup();
  }
}

// Export singleton instance
const onlineStatusService = new OnlineStatusService();

export { onlineStatusService };
export default onlineStatusService;
